// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TySupportTicket } from "@/types";
import SupportTicketCard from "@/UI/cards/SupportTicketCard";
import ActionsBar from "@/UI/components/ActionsBar";
import EmptyState from "@/UI/components/EmptyState";
import Paginator from "@/UI/components/Paginator";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import SupportFormPopup from "@/UI/popups/SupportFormPopup";
import SupportTicketPopup from "@/UI/popups/SupportTicketPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyResponse = { tickets: TySupportTicket[]; total: number; open: number; openMax: number };

/**
 * Where a company asks Lozicore for something. `ai.plans/support-feature.md` is the design.
 *
 * **Every role reaches it and everybody sees all of the company's tickets** — a request for help is
 * the company's business, and a member who could not see what a colleague already reported would
 * report it again. What a member cannot do is tidy away somebody else's: `canHide` below is that
 * rule, and the API enforces the same one.
 *
 * There is no status filter and no search. A company has a handful of these, the newest matters most,
 * and a filter bar over four cards is furniture. The cap is the only state worth saying out loud, so
 * it is said at the top rather than discovered by pressing the button.
 *
 * The tiles are a grid like every other list in this app, and a tile opens the ticket in a popup —
 * prose is read on one wide column, not in three narrow ones side by side.
 */
export default function SupportScreen() {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);
    const { isMember } = usePermission();
    const me = useAuth((state) => state.client);
    const pageSize = usePageSize((state) => state.pageSize);

    const [page, setPage] = useState(1);

    const list = useFetch<TyResponse>("/support", { query: { page, limit: pageSize } }, false);
    const hide = useFetch("/support", {}, false);

    useEffect(() => {
        list.invoke({ query: { page, limit: pageSize } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    useEffect(() => {
        if (!hide.succeededAt) return;
        list.invoke({ query: { page, limit: pageSize } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hide.succeededAt]);

    const data = list.data;
    const tickets = data?.tickets ?? [];
    /** The company's, never the person's: five members cannot have five each. */
    const isCapped = !!data && data.open >= data.openMax;

    const openForm = () =>
        addComp({
            name: "support-form",
            Component: SupportFormPopup,
            anim: "scale",
            details: { onDone: () => list.invoke({ query: { page: 1, limit: pageSize } }) },
        });

    const confirmHide = (ticket: TySupportTicket) =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.support.hide,
                message: t.support.confirmHide,
                confirmLabel: t.support.hide,
                onConfirm: () => hide.invoke({ method: "DELETE", path: `/support/${ticket._id}` }),
            },
        });

    /** A member tidies their own away; an owner or a manager may tidy any of the company's. */
    const canHide = (ticket: TySupportTicket) => {
        if (!isMember) return true;
        const author = typeof ticket.client === "object" && ticket.client ? ticket.client._id : ticket.client;
        return author === me?._id;
    };

    const openTicket = (ticket: TySupportTicket) =>
        addComp({
            name: "support-ticket",
            Component: SupportTicketPopup,
            anim: "scale",
            details: { ticket, onHide: canHide(ticket) ? () => confirmHide(ticket) : undefined },
        });

    return (
        <section className="support-screen">
            <ActionsBar addLabel={isCapped ? undefined : t.support.ask} onAdd={isCapped ? undefined : openForm} />

            <p className="support-screen__note">{t.support.hint}</p>

            {/* Said before the button is missing, not after it has been pressed. */}
            {isCapped && (
                <p className="support-screen__note support-screen__note--capped">{t.support.capped.replace("{max}", String(data?.openMax ?? 0))}</p>
            )}

            {list.loading && !tickets.length && <p className="support-screen__state">{t.common.loading}</p>}
            {(list.error || hide.error) && <p className="support-screen__state support-screen__state--error">{list.error ?? hide.error}</p>}

            {!list.loading && !list.error && !tickets.length && (
                <EmptyState
                    icon="support"
                    title={t.support.empty}
                    description={t.support.emptyHint}
                    actionLabel={isCapped ? undefined : t.support.ask}
                    onAction={isCapped ? undefined : openForm}
                />
            )}

            <div className="support-screen__grid">
                {tickets.map((ticket) => (
                    <SupportTicketCard
                        key={ticket._id}
                        ticket={ticket}
                        onOpen={() => openTicket(ticket)}
                        onHide={canHide(ticket) ? () => confirmHide(ticket) : undefined}
                    />
                ))}
            </div>

            <Paginator page={page} total={data?.total ?? 0} count={tickets.length} onPage={setPage} />
        </section>
    );
}
